import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ChevronRight,
  Star,
  Users,
  Award,
  CheckCircle,
  Phone,
  Download,
  Send,
  Camera,
  Plus,
  Minus,
  CircleDot,
} from "lucide-react";
import GetInTouchUs from "../../Components/Common/GetInTouchUs";
import axios from "axios";
import { toast } from "react-toastify";
import emailjs from "@emailjs/browser";
import LeadGenerationModal from "./Programs/LeadGenerationModal";

const CoursePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const [courseData, setCourseData] = useState({});

  const [activeAccordion, setActiveAccordion] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true);

        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/courses/get-course-by-id/${id}`,
        );

        setCourseData(response.data);
        // console.log("first", response.data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCourse();
    }
  }, [id]);
  // console.log("courseData", courseData);

  const faqs = courseData?.faqs?.length ? courseData.faqs[0].questions : [];

  const handleAccordionToggle = (index) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts correcting the field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleClear = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      message: "",
    });

    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};

    // -------------------------
    // Name validation
    // -------------------------
    const name = formData.name?.trim() || "";

    if (!name) {
      newErrors.name = "Name is required.";
    } else if (name.length < 2) {
      newErrors.name = "Name must be at least 2 characters.";
    } else if (!/^[A-Za-z\s.'-]+$/.test(name)) {
      newErrors.name = "Please enter a valid name.";
    }

    // -------------------------
    // Email validation
    // -------------------------
    const email = formData.email?.trim() || "";

    if (!email) {
      newErrors.email = "Email is required.";
    } else if (
      !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email)
    ) {
      newErrors.email = "Please enter a valid email address.";
    }

    // -------------------------
    // Phone validation
    // -------------------------
    const phone = formData.phone?.trim().replace(/\s+/g, "") || "";

    if (!phone) {
      newErrors.phone = "Phone number is required.";
    } else if (!/^[6-9]\d{9}$/.test(phone)) {
      newErrors.phone = "Please enter a valid 10-digit phone number.";
    }

    // -------------------------
    // Message validation
    // -------------------------
    const message = formData.message?.trim() || "";

    if (!message) {
      newErrors.message = "Message is required.";
    } else if (message.length < 10) {
      newErrors.message = "Message must be at least 10 characters.";
    } else if (message.length > 1000) {
      newErrors.message = "Message cannot exceed 1000 characters.";
    }

    // -------------------------
    // Set validation errors
    // -------------------------
    setErrors(newErrors);

    // Stop submission
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fix the errors in the form.");
      return;
    }

    // -------------------------
    // Send Email
    // -------------------------
    try {
      setIsLoading(true);

      const emailResponse = await emailjs.send(
        "service_lw92dka",
        "template_d23bf2h",
        {
          name,
          email,
          phone,
          message,

          // Course information
          courseName: courseData?.title || "",
          courseId: id || "",
        },
        "Ic_RaxZmAKBkf8h5i",
      );

      console.log("Email Success:", emailResponse);

      toast.success("Your request has been submitted successfully!");

      handleClear();
    } catch (error) {
      console.error("EmailJS Error:", error);

      toast.error(
        "Something went wrong while submitting your request. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCourseContentBrochure = () => {
    if (!courseData?.courseBrochure) {
      toast.info("Course content brochure not available");
      return;
    }

    setSelectedPlan({
      name: `${courseData?.title} - Course Content`,
      brochure: courseData.courseBrochure,
    });

    setIsModalOpen(true);
  };

  const handleBrochureClick = (plan) => {
    if (!plan?.brochure) {
      toast.info("Brochure not available");
      return;
    }

    setSelectedPlan({
      ...plan,
      name: plan?.name || plan?.planName || "Course Brochure",
      brochure: plan.brochure,
    });

    setIsModalOpen(true);
  };

  const handleLearnMore = (plan) => {
    setSelectedPlan(plan);
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center text-white">
        Loading...
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-[#131F22] lg:pt-[70px]">
        {/* Hero Banner */}
        <div className="relative bg-gradient-to-r from-blue-900 via-purple-900 to-indigo-900 text-white overflow-hidden  px-6 lg:px-12">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/30 to-purple-600/30"></div>
          <div className="relative container mx-auto px-6 py-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="inline-flex items-center bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <Star className="w-4 h-4 text-yellow-400 mr-2" />
                  <span className="text-sm">
                    {courseData?.totalPeopleRatings} Rating
                  </span>
                  <Users className="w-4 h-4 ml-4 mr-2" />
                  <span className="text-sm">
                    {courseData?.totalLearners} Learners
                  </span>
                </div>
                <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                  {courseData?.title}
                </h1>
                <p className="text-xl text-gray-200 leading-relaxed">
                  {courseData?.description}
                </p>
                {/* <div className="flex flex-wrap gap-4">
                  <button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg">
                    Start Learning Now
                  </button>
                  <button className="border-2 border-white/30 hover:border-white/50 backdrop-blur-sm px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 hover:bg-white/10">
                    Watch Preview
                  </button>
                </div> */}
              </div>
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-3xl blur-3xl opacity-30"></div>
                <img
                  src={courseData?.courseImage}
                  alt="Course Preview"
                  className="relative w-full h-[400px] rounded-3xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Course Overview Section */}
        <div className="container mx-auto px-6 lg:px-12 pt-20">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-3xl blur-xl opacity-20 group-hover:opacity-30 transition-opacity duration-300"></div>
              <img
                src={courseData?.courseImage}
                alt="Course Overview"
                className="relative w-full h-80  rounded-3xl shadow-xl"
              />
            </div>
            <div className="space-y-8">
              <div>
                <h2 className="text-4xl font-bold text-gray-100 mb-6">
                  {courseData?.title} Course Overview
                </h2>
                <p className="text-lg text-gray-300 leading-relaxed">
                  {courseData?.overview?.description}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="items-center text-center m-10 ">
          <h3 className="text-3xl font-bold text-gray-100 mb-6">
            Key Features
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {courseData?.overview?.keyFeatures?.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <p className="text-gray-700 leading-relaxed">{feature}</p>
              </div>
            ))}
          </div>
        </div>
        {/* Skills Covered Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 py-20">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Left section: skills */}
              <div>
                <h2 className="text-4xl font-bold text-gray-800 mb-8 text-center">
                  Skills Covered
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {courseData?.skillsCovered?.map((skill, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-md hover:shadow-lg hover:scale-105 transform transition-all duration-300"
                    >
                      <CircleDot className="w-6 h-6 text-orange-500 flex-shrink-0" />
                      <span className="text-gray-700 font-semibold text-lg tracking-wide">
                        {skill}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right section: image with gradient overlay */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-3xl blur-3xl opacity-20"></div>
                <img
                  src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=600&h=400&fit=crop"
                  alt="Skills"
                  className="relative w-full h-full object-cover rounded-3xl shadow-xl"
                />
              </div>
            </div>
          </div>
        </div>
        {/* Subscription Plans */}
        <div className="container  mx-auto px-6 py-20">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-100 mb-4">
              Choose Your Learning Path
            </h2>
            <p className="text-xl text-gray-300">
              Select the plan that best fits your goals and budget
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {courseData?.memberships?.map((plan, index) => {
              const planNames = ["Growth", "Professional", "Switch"];
              const planDescriptions = [
                "Fresh Graduates & Early-Career Professionals",
                "Engineered for IT Professionals Aiming Higher",
                "Make the Move: From Any Industry to IT",
              ];
              const isPopular = index === 1;

              return (
                <div
                  key={index}
                  className={`relative bg-white rounded-3xl shadow-xl overflow-hidden h-[600px] flex flex-col ${
                    isPopular ? "ring-4 ring-green-500 ring-opacity-50" : ""
                  }`}
                >
                  {isPopular && (
                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-green-500 to-green-600 text-white text-center py-1.5 font-semibold text-sm">
                      Most Popular
                    </div>
                  )}
                  <div
                    className={`bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 ${isPopular ? "pt-10" : ""}`}
                  >
                    <h3 className="text-xl font-bold mb-1 text-center">
                      {plan.name || planNames[index]}
                    </h3>
                  </div>
                  <div className="p-6 flex flex-col flex-grow">
                    <div className="text-center mb-4">
                      <div className="relative inline-block">
                        <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-green-600  rounded-full flex items-center justify-center shadow-lg">
                          <span className="text-xl font-bold text-white">
                            ${plan.price}
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-600 mt-2 text-sm">
                        {planDescriptions[index]}
                      </p>
                    </div>
                    <div className="space-y-2 mb-4 overflow-y-auto flex-grow">
                      {plan.facilities?.map((facility, fIndex) => (
                        <div key={fIndex} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 text-sm">
                            {facility}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="space-y-2 mt-auto">
                      <button
                        onClick={() => {
                          navigate(
                            `/enrollment?type=course&itemId=${courseData._id}&packageId=${plan._id}`,
                          );
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-500 text-white py-2 rounded-lg font-semibold text-sm transition-all duration-300 transform hover:scale-105"
                      >
                        Enroll Now
                      </button>
                      <button
                        onClick={() => handleBrochureClick(plan)}
                        className="w-full border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white py-2 rounded-lg font-semibold text-sm transition-all duration-300"
                      >
                        Download Brochure
                      </button>
                      <button
                        onClick={handleCourseContentBrochure}
                        className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-500 text-white py-2 rounded-lg font-semibold text-sm transition-all duration-300 transform hover:scale-105"
                      >
                        <Download className="w-5 h-5" />
                        Course Content Brochure
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Requirements Section */}
        <div className="bg-gray-50 py-20">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-8">
              {courseData?.requirements?.map((req, index) => (
                <div
                  key={index}
                  className="bg-white p-8 rounded-3xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center">
                      {index === 0 ? (
                        <Award className="w-8 h-8 text-white" />
                      ) : (
                        <Users className="w-8 h-8 text-white" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-800 mb-4">
                        {req.name}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {req.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Contact and Info Request Section */}
        <div className="container mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-3xl blur-2xl opacity-20"></div>
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop"
                alt="Contact Us"
                className="relative w-full h-96 object-cover rounded-3xl shadow-xl"
              />
              <div className="absolute bottom-8 left-8 right-8 bg-white/90 backdrop-blur-sm rounded-2xl p-6">
                <div className="flex items-center gap-4 mb-4">
                  <Phone className="w-6 h-6 text-blue-600" />
                  <div>
                    <p className="text-gray-600">Contact Us</p>
                    <p className="text-xl font-bold">+1 470 740 2777</p>
                  </div>
                </div>
                {/* <div className="space-y-3 flex gap-4">
                  <button className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 rounded-xl font-semibold hover:from-orange-600 hover:to-red-600 transition-all duration-300">
                    <Download className="w-5 h-5 inline mr-2" />
                    Prospectus
                  </button>
                  <button className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-indigo-600 transition-all duration-300">
                    <Download className="w-5 h-5 inline mr-2" />
                    Course Content
                  </button>
                </div> */}
              </div>
            </div>
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-8 rounded-3xl shadow-lg">
              <h3 className="text-3xl font-bold text-gray-800 text-center mb-8">
                Request More Information
              </h3>
              <div className="space-y-6">
                {/* Name */}
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter Your Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`w-full px-6 py-4 border rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 ${
                      errors.name ? "border-red-500" : "border-gray-200"
                    }`}
                  />

                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1 ml-2">
                      {errors.name}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter Your Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-6 py-4 border rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 ${
                      errors.email ? "border-red-500" : "border-gray-200"
                    }`}
                  />

                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1 ml-2">
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Enter Phone Number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    maxLength={10}
                    className={`w-full px-6 py-4 border rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 ${
                      errors.phone ? "border-red-500" : "border-gray-200"
                    }`}
                  />

                  {errors.phone && (
                    <p className="text-red-500 text-sm mt-1 ml-2">
                      {errors.phone}
                    </p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <textarea
                    name="message"
                    rows={3}
                    maxLength={1000}
                    placeholder="Enter your message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className={`w-full px-6 py-4 border rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 resize-none ${
                      errors.message ? "border-red-500" : "border-gray-200"
                    }`}
                  />

                  <div className="flex justify-between items-start">
                    {errors.message ? (
                      <p className="text-red-500 text-sm mt-1 ml-2">
                        {errors.message}
                      </p>
                    ) : (
                      <span />
                    )}

                    <span className="text-gray-400 text-xs mt-1">
                      {formData.message.length}/1000
                    </span>
                  </div>
                </div>

                {/* Submit */}
                <div className="flex justify-center">
                  <button
                    type="submit"
                    disabled={isLoading}
                    onClick={handleSubmit}
                    className="bg-gradient-to-r from-gray-800 to-gray-900 hover:from-gray-900 hover:to-black disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 flex items-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why Online Bootcamp Section */}
        <div className="relative bg-gradient-to-r from-gray-900 via-blue-900 to-indigo-900 text-white py-20 overflow-hidden">
          <div className="absolute inset-0 bg-black/30"></div>
          <div className="relative container mx-auto px-6">
            <div className="mb-16 text-center">
              <h2 className="text-5xl lg:text-6xl font-bold mb-6">
                Why Online Bootcamp
              </h2>
            </div>
            <div className="grid xl:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                {courseData?.whyOnlineBootcamp?.map((item, index) => (
                  <div
                    key={index}
                    className="flex gap-6 p-6 bg-white/10 backdrop-blur-sm rounded-2xl hover:bg-white/20 transition-all duration-300"
                  >
                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-3">{item.name}</h3>
                      <p className="text-gray-300 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="relative">
                {courseData?.courseVideo ? (
                  <div className="relative aspect-video w-full rounded-3xl overflow-hidden shadow-2xl">
                    <iframe
                      className="w-full h-full"
                      src={courseData.courseVideo}
                      title="Course Preview Video"
                      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="aspect-video w-full bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl flex flex-col items-center justify-center border-2 border-dashed border-gray-600">
                    <Camera className="w-16 h-16 text-gray-400 mb-4" />
                    <h3 className="text-2xl font-bold text-gray-300 mb-2">
                      No Course Video
                    </h3>
                    <p className="text-gray-400 text-center max-w-sm">
                      The video preview for this course is currently
                      unavailable. Please check back later.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="container mx-auto px-6 py-20 bg-gray-600">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-100 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-300 lg:w-3/5 mx-auto">
              Find quick answers to common questions about our programs,
              services, and support.
            </p>
          </div>
          <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden"
              >
                <button
                  onClick={() => handleAccordionToggle(index)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 transition-colors duration-300"
                >
                  <span className="text-lg font-semibold text-gray-800 group-hover:text-blue-600 transition-colors duration-300">
                    {faq.question}
                  </span>
                  {activeAccordion === index ? (
                    <Minus className="w-6 h-6 text-blue-600 transition-transform duration-300 transform rotate-180" />
                  ) : (
                    <Plus className="w-6 h-6 text-gray-400 group-hover:text-blue-600 transition-colors duration-300" />
                  )}
                </button>
                {activeAccordion === index && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-600 leading-relaxed transition-all duration-300 ease-in-out">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA Section */}
        {/* <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-20">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold mb-6">
              Ready to Start Your Journey?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Join thousands of successful graduates and transform your career
              today
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <Link
                to="/enrollment"
                className="bg-white text-orange-500 hover:bg-gray-100 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105"
              >
                Enroll Now
              </Link>
              <Link
                to="/serviceRequest"
                className="border-2 border-white hover:bg-white hover:text-orange-500 px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300"
              >
                Contact Advisor
              </Link>
            </div>
          </div>
        </div> */}
        <div className="lg:px-20 md:px-10 px-5 pb-10">
          <GetInTouchUs />
        </div>
      </div>

      <LeadGenerationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedPlan={selectedPlan}
      />
    </>
  );
};

export default CoursePage;
